import { env } from '@/lib/env';

/** The slice of Midtrans `snap.js` this app calls. */
export interface SnapResult {
  order_id?: string;
  transaction_status?: string;
  status_code?: string;
}

interface SnapCallbacks {
  embedId: string;
  onSuccess?: (result: SnapResult) => void;
  onPending?: (result: SnapResult) => void;
  onError?: (result: SnapResult) => void;
  onClose?: () => void;
}

export interface SnapApi {
  embed: (token: string, options: SnapCallbacks) => void;
  hide: () => void;
}

declare global {
  interface Window {
    snap?: SnapApi;
  }
}

let loading: Promise<SnapApi> | null = null;

/**
 * Load Midtrans `snap.js` once, with the merchant's client key.
 *
 * The client key MUST belong to the same merchant as the backend's server key:
 * a token minted by one merchant does not open under another's key, and Snap
 * reports that only as a generic error inside its own frame.
 *
 * A failed load is not cached, so the next payment tries again rather than
 * staying broken until a page reload.
 */
export function loadSnap(): Promise<SnapApi> {
  if (window.snap) return Promise.resolve(window.snap);
  if (loading) return loading;
  if (!env.MIDTRANS_CLIENT_KEY) {
    return Promise.reject(
      new Error('VITE_MIDTRANS_CLIENT_KEY belum diatur — halaman Snap tidak dapat dibuka.'),
    );
  }

  loading = new Promise<SnapApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = env.MIDTRANS_SNAP_URL;
    script.setAttribute('data-client-key', env.MIDTRANS_CLIENT_KEY);
    script.async = true;
    script.onload = () => {
      if (window.snap) resolve(window.snap);
      else reject(new Error('Snap dimuat tetapi tidak tersedia.'));
    };
    script.onerror = () => {
      script.remove();
      reject(new Error('Gagal memuat Snap Midtrans. Periksa koneksi internet.'));
    };
    document.head.appendChild(script);
  }).catch((err: unknown) => {
    loading = null;
    throw err;
  });

  return loading;
}
